"use client"
import { createContext, useContext, useState, useLayoutEffect, useCallback, useMemo, type ReactNode } from "react"

interface LayoutVisibility {
  showHeader: boolean
  showFooter: boolean
}

interface LayoutVisibilityContextValue extends LayoutVisibility {
  setVisibility: (v: Partial<LayoutVisibility>) => void
}

const LayoutVisibilityContext = createContext<LayoutVisibilityContextValue>({
  showHeader: true,
  showFooter: true,
  setVisibility: () => {},
})

// EGDE-612: the initial values are computed on the server (see the
// [[...dynamic]] segment layout) so the very first paint already matches the
// page's CMS hideHeader/hideFooter flags. Defaulting them to `true` here would
// ship the chrome in the HTML and only remove it once `LayoutConfig`'s effect
// runs after hydration, which is the flash + reflow QA reported on slow links.
export function LayoutVisibilityProvider({
  children,
  initialShowHeader = true,
  initialShowFooter = true,
}: {
  children: ReactNode
  initialShowHeader?: boolean
  initialShowFooter?: boolean
}) {
  const [visibility, setVisibilityState] = useState<LayoutVisibility>({
    showHeader: initialShowHeader,
    showFooter: initialShowFooter,
  })

  const setVisibility = useCallback((v: Partial<LayoutVisibility>) => {
    setVisibilityState((prev) => ({ ...prev, ...v }))
  }, [])

  const contextValue = useMemo(
    () => ({ ...visibility, setVisibility }),
    [visibility, setVisibility],
  )

  return (
    <LayoutVisibilityContext.Provider value={contextValue}>
      {children}
    </LayoutVisibilityContext.Provider>
  )
}

export function useLayoutVisibility() {
  return useContext(LayoutVisibilityContext)
}

// EGDE-612: first paint is now driven by the server-seeded provider values, so
// this component only exists to correct the chrome on client-side navigations,
// where the already-mounted provider keeps the previous page's state. Its
// cleanup restores both to visible when leaving a chrome-less page.
export function LayoutConfig({
  hideHeader,
  hideFooter,
}: {
  hideHeader?: boolean | null
  hideFooter?: boolean | null
}) {
  const { setVisibility } = useLayoutVisibility()

  useLayoutEffect(() => {
    setVisibility({
      showHeader: !hideHeader,
      showFooter: !hideFooter,
    })
    return () => {
      setVisibility({ showHeader: true, showFooter: true })
    }
  }, [hideHeader, hideFooter, setVisibility])

  return null
}
